import { useNotification } from "@context/NotificationContext";
import type { CancelPortServicePayload } from "@services/port-services/types";
import { cancelPortService } from "@services/portServicesApi";
import { useMutation } from "@tanstack/react-query";
import { runTransaction } from "@telemetry/runTransaction";
import { t } from "i18next";
import { generatePrefixedNotifyList } from "../preview/utils";

interface UsePortServiceProps {
  cancellationReason: string;
  atc: string | Date;
  portServiceId: string;
  notifyList: string[];
  setShouldRefetch: (value: boolean) => void;
}
const LANG_BASE = "vessel-call.port-services";

export interface UsePortServiceResult {
  onSubmit: () => Promise<void>;
}

export const useCancelPortService = (props: UsePortServiceProps): UsePortServiceResult => {
  const { cancellationReason, portServiceId, atc, notifyList, setShouldRefetch } = props;

  const { notify } = useNotification();
  const cancelService = useMutation<unknown, unknown, CancelPortServicePayload>({
    mutationFn: (payload) => {
      return runTransaction(
        {
          module: "vessel_call",
          screen: "PortServiceDetails",
          action: "cancel_port_service",
          entityType: "port_service",
        },
        (transactionId) =>
          cancelPortService(
            "bff-vessel-call",
            portServiceId,
            payload,
            ...(transactionId ? [transactionId] : []),
          ),
      );
    },
    onSuccess: () => {
      setShouldRefetch?.(true);
      notify({
        title: t(`${LANG_BASE}.notifications.service-cancelled-success-title`),
        description: t(`${LANG_BASE}.notifications.service-cancelled-success-description`),
        type: "success",
      });
    },
    onError: (error) => {
      notify({
        title: t(`${LANG_BASE}.notifications.service-cancelled-error-title`),
        description: t(`${LANG_BASE}.notifications.service-cancelled-error-description`),
        type: "error",
      });
      console.error("Error submitting port service request:", error);
    },
  });

  const onSubmit = async (): Promise<void> => {
    const notificationFor = generatePrefixedNotifyList("CANCEL", notifyList);
    const payload = {
      cancellationReason,
      notificationFor,
      atc,
    };
    cancelService.mutate(payload);
  };
  return { onSubmit };
};
